import React from "react";
import store from "../../state/store";
import Events from "../../common/helpers/event-helpers/events";
import { getInitialData } from "../../state/actions/getInitialData";
import { setError } from "../../state/actions/setError";
import { RenderUrl } from "../../components/atoms/RenderUrl";
import Loader from "../../components/Loader";
import Pagination from "../../components/Pagination";
import { RenderRecentActivities } from "../../components/RecentActivity";
import DeclarationList from "../../components/declaration/DeclareInterestList";
import RecentComments from "../community-contributions/RecentComments";
import SSOUser from "../../components/social/SSOUser/SSOUser";
import IconExternalLink from "../../../../assets/images/svg/external-link.svg";
import { parseLocation } from "./helpers";
import { OBJECT_TYPES, RESULTS_PER_PAGE } from "./constants";
import { httpRequest } from "../../services/http";
import { redPink, whiteColor } from "../../common/helpers/colors";
import HelperText from "../../components/atoms/HelperText";

const { varsomeConfig, gdpr_page } = window;

export default class Profile extends React.Component {
  constructor() {
    super();
    this.screen_name = null;
    this.objectStateStructure = {
      loading: false,
      error: null,
      results: null,
      total: 0,
      page: 1,
    };
    this.state = this.setInitialObjectState();
  }

  componentDidMount() {
    this.screen_name = parseLocation();
    const currentState = this.state;
    if (this.screen_name) {
      Events.subscribe("FLAG_CHANGED", this, this.getActivityByObject, [
        "flag",
        currentState.flag ? currentState.flag.page : 1,
      ]);
      Events.subscribe("PUBMED_CHANGED", this, this.getActivityByObject, [
        "pubmed",
        currentState.pubmed ? currentState.pubmed.page : 1,
      ]);
      getInitialData(this, this.getProfile, [this.screen_name]);
    } else {
      setError("User screen name not provided");
    }
  }

  componentDidUpdate() {
    const screenName = parseLocation();
    if (screenName !== this.screen_name) {
      this.screen_name = screenName;
      store.dispatch({ type: "SET_ERROR", error: null });

      this.setState({
        profile: null,
        profileLoaded: false,
        ...this.setInitialObjectState(),
      });
      getInitialData(this, this.getProfile, [this.screen_name]);
    }
  }

  setInitialObjectState() {
    const initialState = {};
    for (let i = 0; i < OBJECT_TYPES.length; i++) {
      initialState[OBJECT_TYPES[i].name] = { ...this.objectStateStructure };
    }
    return initialState;
  }

  getActivityByObject(objType, page = 1) {
    const start = (page - 1) * 10;
    const end = (page - 1) * 10 + 10;

    const currentState = this.state;
    const objState = {};
    objState[objType] = { ...this.objectStateStructure, loading: true };
    this.setState(objState);
    const state = store.getState();

    const { user_id: userId } = currentState.profile;
    const url = `${state.apiUrl}/user_contributions/${objType}/${userId}?start=${start}&end=${end}`;
    httpRequest
      .get(url)
      .then((response) => {
        const { data } = response;
        objState[objType].results = data.results;
        objState[objType].total = data.total_items;
        objState[objType].page = page;
      })
      .catch((error) => {
        console.error("While getting user activity):", error);
        objState[objType].error =
          "Could not process this request. Refresh the page or contact support.";
      })
      .finally(() => {
        objState[objType].loading = false;
        this.setState(objState);
      });
  }

  getUserActivity() {
    for (let i = 0; i < OBJECT_TYPES.length; i++) {
      this.getActivityByObject(OBJECT_TYPES[i].name);
    }
  }

  getProfile(screenName) {
    const state = store.getState();
    const url = `${state.varsomeHost}/profile/${screenName}`;
    httpRequest
      .get(url)
      .then((response) => {
        this.setState({
          profileLoaded: true,
          profile: response.data,
        });
        this.getUserActivity();
      })
      .catch((error) => {
        const errorResponse =
          typeof error?.response === "object" ? error.response : {};
        const { status } = errorResponse;
        if (status === undefined) {
          console.error("Error in get profile request", error);
        }
        console.error("While getting profile: ", error);
        if (status === 401) {
          setError(
            `You are not authorized to view this resource. Please <a href="/sign-in/?next=${window.location.href}">Sign in</a> to continue, or <a href="/sign-up/">Join</a> for free.`
          );
        } else if (status === 404) {
          setError("Could not find a profile for this screen name.");
        } else {
          setError(
            "Could not process this request. Try again or contact support."
          );
        }
        this.setState({ profileLoaded: true });
      });
  }

  getPageLink(objType, p) {
    this.getActivityByObject(objType, p);
  }

  render() {
    const { profileLoaded, error, profile, ...localState } = this.state;
    const { user_id: userId } = profile || {};
    const {
      userId: storeUserId,
      varsomeHost,
      userScreenName,
    } = store.getState();
    let tmpl;
    if (profileLoaded && !error) {
      if (profile) {
        tmpl = (
          <div id="profile-page">
            <div className="varsome-result-fixed">
              <div className="varsome-container">
                <div className="varsome-title">
                  User profile <HelperText fieldDescriptionKey="User profile" />
                  {userScreenName === profile.screen_name && (
                    <div className="varsome-right">
                      <RenderUrl
                        url={`${varsomeHost}/account-settings/`}
                        label={
                          <span>
                            <i className="fa fa-edit" /> Edit your profile
                          </span>
                        }
                      />
                    </div>
                  )}
                </div>
                <div className="varsome-inner-container">
                  <h4>
                    {profile.first_name} {profile.last_name}
                    {profile.name_suffix && (
                      <span style={{ fontSize: "1rem" }}>
                        , {profile.name_suffix}
                      </span>
                    )}
                  </h4>
                  <p className="premium-badge-container">
                    Account Type:
                    <img
                      src={`${varsomeConfig.static_url}img/premium-badge.png`}
                    />
                  </p>
                  <SSOUser
                    ssoId={userId}
                    UserRenderer={({ resolvedUser }) => {
                      return resolvedUser ? (
                        <div>
                          {resolvedUser.organisation?.organisation_name && (
                            <div className="tw-flex tw-items-baseline">
                              <span className="label">Organization:</span>
                              {resolvedUser.organisation?.organisation_name}
                              {resolvedUser.organisation?.country?.country && (
                                <span>
                                  ,{" "}
                                  {resolvedUser.organisation?.country?.country}
                                </span>
                              )}
                              {resolvedUser.organisation?.uri && (
                                <a
                                  className="tw-inline-block tw-ml-1"
                                  href={resolvedUser.organisation.uri}
                                  target="_blank"
                                  title="Visit Website"
                                >
                                  <IconExternalLink className="icon tw-block" />
                                </a>
                              )}
                            </div>
                          )}
                          {resolvedUser.organisation?.organisation_type && (
                            <div>
                              <span className="label">Type:</span>
                              {resolvedUser.organisation?.organisation_type}
                            </div>
                          )}
                        </div>
                      ) : null;
                    }}
                  />
                  {profile.summary && (
                    <div>
                      <span className="label">Summary:</span> {profile.summary}
                    </div>
                  )}
                  <div className="tw-flex tw-mt-2">
                    {profile.linkedin_url && (
                      <p className="social-link tw-mb-0">
                        <a href={profile.linkedin_url} target="_blank">
                          LinkedIn
                        </a>
                      </p>
                    )}
                    {profile.researchgate_url && (
                      <p className="social-link tw-mb-0">
                        <a href={profile.researchgate_url} target="_blank">
                          ResearchGate
                        </a>
                      </p>
                    )}
                  </div>
                  {storeUserId === profile.user_id && (
                    <div>
                      <button
                        type="button"
                        className="gdpr-btn"
                        onClick={() =>
                          gdpr_page.show_modal(true, false, "gdpr-page")
                        }
                      >
                        Show Consents
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {storeUserId === profile.user_id && (
              <div className="varsome-result-fixed">
                <div className="varsome-container">
                  <div className="varsome-title">
                    Your open connection requests
                  </div>
                  <div className="varsome-inner-container">
                    <DeclarationList
                      userId={profile.user_id}
                      emptyDeclarationMessage="No open connection requests."
                    />
                  </div>
                </div>
              </div>
            )}
            {OBJECT_TYPES.map((objType) => {
              const objectData = localState[objType.name];
              let total = 0;
              if (typeof objectData !== "undefined") {
                total = objectData.total;
              }
              const maxPage = Math.ceil(total / RESULTS_PER_PAGE);

              return (
                objectData && (
                  <div className="varsome-result-fixed" key={objType.name}>
                    <div className="varsome-container">
                      <div className="varsome-title">
                        User {objType.description}
                      </div>
                      {objectData.error && (
                        <div className="varsome-inner-container">
                          <div
                            className="varsome-text-center tw-py-4 tw-px-0"
                            style={{
                              color: whiteColor,
                              backgroundColor: redPink,
                            }}
                          >
                            {objectData.error}
                          </div>
                        </div>
                      )}
                      {objectData.loading && (
                        <div className="varsome-inner-container varsome-text-center">
                          <Loader size="small" />
                        </div>
                      )}
                      {!objectData.loading && (
                        <div className="varsome-inner-container">
                          {Array.isArray(objectData.results) &&
                            objectData.results.length > 0 && (
                              <RenderRecentActivities
                                activities={objectData.results}
                                separator={true}
                                renderDate={true}
                                renderEdit={true}
                                userId={userId}
                                // eslint-disable-next-line react/jsx-no-bind
                                getComments={this.getActivityByObject.bind(
                                  this,
                                  "comment",
                                  objectData.page
                                )}
                              />
                            )}
                          {Array.isArray(objectData.results) &&
                            objectData.results.length === 0 && (
                              <div>No {objType.description} yet.</div>
                            )}
                          {maxPage > 1 && (
                            <div className="varsome-inner-container tw-pb-0 tw-pt-4">
                              <Pagination
                                curPage={objectData.page}
                                maxPage={maxPage}
                                pagesAround={2}
                                // eslint-disable-next-line react/jsx-no-bind
                                getPageLink={this.getPageLink.bind(
                                  this,
                                  objType.name
                                )}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              );
            })}
            {userId ? (
              <div className="varsome-result-fixed">
                <div className="varsome-container">
                  <div className="varsome-title">User Comments</div>
                  <RecentComments ssoId={userId} canEdit={true} />
                </div>
              </div>
            ) : null}
          </div>
        );
      }
    }

    if (!profileLoaded && error) {
      // dangerouslySetInnerHTML is needed here because error is in HTML
      tmpl = (
        <div
          className="varsome-request-error"
          id="varsome-request-error"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: error }}
        />
      );
    }

    if (!profileLoaded && !error) {
      tmpl = (
        <div className="varsome-text-center">
          <Loader />
        </div>
      );
    }

    return tmpl || null;
  }
}
